import { FC } from 'react';
import { Avatar } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';

import {
  ADMIN_USER_ADD,
  FIRM_USER_ADD,
  createAdminUserEditRoute,
  createFirmUserEditRoute,
} from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetAdminUsers } from 'hooks/data/adminUser/useAdminUsers';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { useMutateAdminUser } from 'hooks/data/adminUser/useAdminUser';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import { Email, LockReset } from '@mui/icons-material';
import SearchField from 'further-ui/components/SearchField';
import api from 'lib/trpcClient';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import Row from 'components/Layout/Row';

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Users', isActive: true },
];

const AdminUser: FC = () => {
  const { isSuperAdmin } = useGetRole();
  const deleteDisclosure = useDisclosure<{ id: string }>();
  const resetMfaDisclosure = useDisclosure<{ id: string }>();
  const [
    readPermission,
    createPermission,
    addToFirmPermission,
    editPermission,
    deletePermission,
    removeFromFirmPermission,
    manageUserSecurityPermission,
  ] = useHasPermissions([
    RolePermission.ReadUsers,
    RolePermission.CreateUsers,
    RolePermission.AddUserToFirm,
    RolePermission.EditUsers,
    RolePermission.DeleteUsers,
    RolePermission.RemoveUserFromFirm,
    RolePermission.ManageUserSecurity,
  ]);

  const {
    page,
    rowsPerPage,
    order,
    orderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    toFirstPage,
  } = usePagination({ id: 'users-table', orderBy: 'email' });

  const { filters, handleChangeFilters } = useFilters('users-table', {
    onFiltersChange: toFirstPage,
    defaultFilters: { search: '' },
  });

  // superadmin list and actions
  const { data: users, isFetching } = useGetAdminUsers(
    {
      page: page === 0 ? '1' : page.toString(),
      perPage: rowsPerPage.toString(),
      type: order,
      keyName: orderBy,
      search: filters.search,
    },
    isSuperAdmin && readPermission,
  );
  const { remove, requestPasswordResetForUser, resetMfa } =
    useMutateAdminUser();

  // firm-specific list and actions
  const firmUsers = api.adminUser.listCurrentFirmUsers.useQuery(
    {},
    { enabled: !isSuperAdmin && readPermission },
  );
  const filteredFirmUsers = firmUsers.data?.filter(
    (user) =>
      `${user.firstName} ${user.lastName}`
        .toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.search.toLowerCase()),
  );
  const paginatedFirmUsers = filteredFirmUsers?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const removeFromFirm = api.adminUser.removeUserFromCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User removed successfully.',
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleConfirmDelete = async () => {
    if (isSuperAdmin) {
      remove.mutate(deleteDisclosure.actionProps?.id, {
        onSuccess: () => {
          deleteDisclosure.onClose();
        },
      });
    } else {
      removeFromFirm.mutate({ adminUserId: deleteDisclosure.actionProps?.id });
    }
  };

  const columns = [
    {
      label: 'First name',
      key: 'firstName',
      sort: isSuperAdmin,
      render: (elm) => (
        <Row spacing="sm" centered>
          <Avatar src={isSuperAdmin ? elm?.profilePic : elm?.profilePicUrl} />
          {elm?.firstName}
        </Row>
      ),
    },
    { label: 'Last name', key: 'lastName', sort: isSuperAdmin },
    { label: 'Email', key: 'email', sort: isSuperAdmin },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              color: 'primary',
              link: isSuperAdmin
                ? createAdminUserEditRoute(elm.id)
                : createFirmUserEditRoute(elm.id),
              visible: editPermission,
            },
            {
              label: isSuperAdmin ? 'Delete' : 'Remove user',
              icon: Delete,
              color: 'error',
              onClick: () => deleteDisclosure.stageAction({ id: elm.id }),
              visible: isSuperAdmin
                ? deletePermission
                : removeFromFirmPermission,
            },
            {
              label: 'Send password reset email',
              icon: Email,
              color: 'primary',
              onClick: () => requestPasswordResetForUser.mutate({ id: elm.id }),
              visible: manageUserSecurityPermission,
            },
            {
              label: 'Reset MFA',
              icon: LockReset,
              color: 'primary',
              onClick: () => resetMfaDisclosure.stageAction({ id: elm.id }),
              visible: manageUserSecurityPermission && elm.hasMfaEnrolled,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer
      heading={isSuperAdmin ? 'User Management (all firms)' : 'User Management'}
      breadcrumbs={breadcrumbs}
    >
      <PageContent>
        <PageFilters
          pageId="admin-users-table"
          buttons={[
            {
              visible: isSuperAdmin ? createPermission : addToFirmPermission,
              label: 'Add User',
              link: isSuperAdmin ? ADMIN_USER_ADD : FIRM_USER_ADD,
            },
          ]}
        >
          <Filter>
            <SearchField
              name="search"
              placeholder="Search by name or email"
              fullWidth
              value={filters.search}
              allowEmptyQuery
              autoSearchOnDebounce
              onSearch={(search) => {
                handleChangeFilters({ search });
              }}
            />
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          tablebody={
            isSuperAdmin ? users?.result ?? [] : paginatedFirmUsers ?? []
          }
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          count={
            isSuperAdmin ? users?.total ?? 0 : filteredFirmUsers?.length ?? 0
          }
          pagination={true}
          loading={isFetching || firmUsers.isFetching}
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        title={
          isSuperAdmin
            ? `Are you sure you wish to delete this user?`
            : `Are you sure you wish to remove this user from the firm?`
        }
        content={
          isSuperAdmin ? (
            <p>
              This cannot be undone and all information relating to the user
              will be lost.
            </p>
          ) : (
            <p>They can be added back to the firm in the future.</p>
          )
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: isSuperAdmin ? 'Delete user' : 'Remove user',
        }}
        confirmBtnProps={{
          disabled: remove.isPending || removeFromFirm.isPending,
        }}
        cancelBtnProps={{
          disabled: remove.isPending || removeFromFirm.isPending,
        }}
      />
      <AlertDialog
        open={resetMfaDisclosure.isOpen}
        onClose={resetMfaDisclosure.onClose}
        onConfirm={() =>
          resetMfa.mutate(
            { adminUserId: resetMfaDisclosure.actionProps?.id },
            { onSuccess: () => resetMfaDisclosure.onClose() },
          )
        }
        title="Reset MFA"
        content={
          <p>
            Only use this if a user can no longer access the MFA device they
            registered with. They will be asked to set up a new MFA device when
            they next log in. Any passkeys they have on their devices will
            remain, and can still be used to log in.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Reset MFA',
        }}
        confirmBtnProps={{ disabled: resetMfa.isPending }}
        cancelBtnProps={{ disabled: resetMfa.isPending }}
      />
    </PageContainer>
  );
};

export default AdminUser;
